import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getApiAccessToken } from '@/lib/logto';
import type { Banner, IapUser, IapUserUpdate, Purchase } from '@/types/customization';

/**
 * QLASH Customization API client — banners, purchases, IAP user record.
 *
 * Mirrors `mobile-app/src/services/iapApi.ts`. The microservice expects the
 * same Laravel-issued Passport token we use for the main backend (obtained
 * via `/auth/exchange-token` — see `lib/logto.ts` for the exchange flow).
 *
 * Endpoints used in Phase 1 (read-only banner picker):
 *   - GET    /users/iap/:userId           → IapUser (with currently selected banner)
 *   - GET    /purchases?user=:iapUserId   → owned banners
 *   - PUT    /users/:iapUserId            → switch active banner
 *
 * `GET /banners` (full catalog) is intentionally NOT wired yet — banner
 * purchasing happens on the mobile app via native IAP. The Mini App routes
 * the "Get more banners" CTA to `env.shopUrl` via `openLink`.
 */
export const customizationApi = createApi({
  reducerPath: 'customizationApi',
  tagTypes: ['IapUser', 'Purchases', 'Banners'],
  baseQuery: fetchBaseQuery({
    baseUrl: env.customizationApiUrl,
    prepareHeaders: async (headers) => {
      const token = await getApiAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');
      headers.set('platform', 'telegram');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /**
     * Resolve QLASH user.id → IAP user record. Returns 404 if the user has
     * never interacted with the customization service. Callers should treat
     * a missing record as "no banner selected, no purchases".
     */
    getIapUser: builder.query<IapUser, number | string>({
      query: (userId) => ({ url: `/users/iap/${userId}` }),
      providesTags: (_r, _e, userId) => [{ type: 'IapUser', id: String(userId) }],
    }),
    /**
     * Owned banners for a given IAP user record. Each `purchase.banner` is a
     * fully-hydrated Banner — no need for a second lookup.
     */
    getPurchases: builder.query<Purchase[], { user: string }>({
      query: ({ user }) => ({ url: '/purchases', params: { user } }),
      providesTags: ['Purchases'],
    }),
    /**
     * Set the active banner for the current IAP user. Server validates that
     * the banner id is among the user's purchases.
     */
    updateIapUser: builder.mutation<IapUser, { id: string; body: IapUserUpdate }>({
      query: ({ id, body }) => ({ url: `/users/${id}`, method: 'PUT', body }),
      invalidatesTags: ['IapUser'],
    }),
    /** Full banner catalog (currently unused — kept for future Shop screen). */
    getBanners: builder.query<Banner[], void>({
      query: () => ({ url: '/banners' }),
      providesTags: ['Banners'],
    }),
  }),
});

export const {
  useGetIapUserQuery,
  useGetPurchasesQuery,
  useUpdateIapUserMutation,
  useGetBannersQuery,
} = customizationApi;
